/**
 * Schémas Zod — Quote Requests + Recipients (lecture seule en 9.F).
 *
 * Source DB : `app.quote_requests`, `app.quote_request_recipients` (0021).
 *
 * Q1 PO : `quote_request_recipients` exposé UNIQUEMENT nested dans
 * `GET /v1/quote-requests/:id`. Pas de route racine en 9.F.
 *
 * Pas de Create/Update/Send/Cancel en 9.F — actions engageantes (envoi
 * email externe) reportées Chunk 9.H avec D11 human-gating.
 */

import { z } from "zod";

import { UuidSchema } from "./common.js";

// -----------------------------------------------------------------------------
// Enums (miroir DB)
// -----------------------------------------------------------------------------

export const QuoteRequestStatusSchema = z.enum([
  "draft",
  "sent",
  "partially_received",
  "fully_received",
  "cancelled",
  "expired",
]);
export type QuoteRequestStatus = z.infer<typeof QuoteRequestStatusSchema>;

export const QuoteRequestRecipientStatusSchema = z.enum([
  "pending",
  "sent",
  "viewed",
  "responded",
  "declined",
  "expired",
  // 9.L.5 — feedback de livraison Mailgun (webhook). Miroir enum DB `app.qrr_status`
  // étendu migration 0041.
  "bounced",
  "unsubscribed",
  "complained",
]);
export type QuoteRequestRecipientStatus = z.infer<typeof QuoteRequestRecipientStatusSchema>;

// -----------------------------------------------------------------------------
// Row — quote_request_recipients
// -----------------------------------------------------------------------------

export const QuoteRequestRecipientRowSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  quote_request_id: UuidSchema,

  insurer_code: z.string(),
  recipient_email: z.string().nullable(),
  recipient_contact_id: UuidSchema.nullable(),
  language: z.string(),
  status: QuoteRequestRecipientStatusSchema,
  email_block_reason: z.enum(["complained", "unsubscribed"]).nullable().optional(),
  delivery_feedback: z
    .enum(["delivered", "opened", "clicked", "bounced", "complained", "unsubscribed", "failed"])
    .nullable()
    .optional(),

  sent_at: z.string().nullable(),
  responded_at: z.string().nullable(),
  expected_response_by: z.string().nullable(),
  expiry_reason: z.string().nullable(),
  notes: z.string().nullable(),
  insurer_email_note: z.string().nullable().optional(),

  created_at: z.string(),
  updated_at: z.string(),
});
export type QuoteRequestRecipientRow = z.infer<typeof QuoteRequestRecipientRowSchema>;

// -----------------------------------------------------------------------------
// Row — quote_requests
// -----------------------------------------------------------------------------

export const QuoteRequestRowSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  project_id: UuidSchema,

  title: z.string().nullable(),
  insurer_email_note: z.string().nullable().optional(),
  template_instance_id: UuidSchema.nullable(),
  status: QuoteRequestStatusSchema,

  sent_at: z.string().nullable(),
  expected_response_by: z.string().nullable(),
  cancelled_at: z.string().nullable(),
  cancelled_reason: z.string().nullable(),

  created_by_user: UuidSchema.nullable(),
  created_by_agent: UuidSchema.nullable(),

  // 9.M.7 — Forensic visible. LEFT JOIN `app.agent_identities` côté handler
  // expose le `display_name` de l'agent qui a créé la row (si `created_by_agent`
  // non-null). Champ optionnel/nullable :
  //   - absent → fixtures historiques sans jointure (rétrocompat tests).
  //   - null   → row créée par user ou agent supprimé.
  //   - string → display_name lisible côté UI broker badge.
  created_by_agent_display_name: z.string().nullable().optional(),

  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type QuoteRequestRow = z.infer<typeof QuoteRequestRowSchema>;

/**
 * Row de LISTE — 9.BD.1.
 *
 * La projection liste porte de quoi **reconnaître** une demande : le dossier et
 * l'entreprise, pas seulement des identifiants. Un conseiller qui cherche « ma
 * demande en cours » pense au nom de son client, jamais à un UUID.
 *
 * Optionnels et nullables : le détail (`GET /:id`) ne les projette pas, et un
 * dossier peut légitimement n'avoir aucun titre.
 */
export const QuoteRequestListItemSchema = QuoteRequestRowSchema.extend({
  project_title: z.string().nullable().optional(),
  company_name: z.string().nullable().optional(),
});
export type QuoteRequestListItem = z.infer<typeof QuoteRequestListItemSchema>;

// -----------------------------------------------------------------------------
// Composition — GET /v1/quote-requests/:id
// -----------------------------------------------------------------------------

export const QuoteRequestWithRecipientsSchema = QuoteRequestRowSchema.extend({
  recipients: z.array(QuoteRequestRecipientRowSchema),
});
export type QuoteRequestWithRecipients = z.infer<typeof QuoteRequestWithRecipientsSchema>;

// -----------------------------------------------------------------------------
// Filtres listing — GET /v1/quote-requests
// -----------------------------------------------------------------------------

export const QuoteRequestFilterSchema = z.object({
  project_id: UuidSchema.optional(),
  status: QuoteRequestStatusSchema.optional(),
});
export type QuoteRequestFilter = z.infer<typeof QuoteRequestFilterSchema>;

// -----------------------------------------------------------------------------
// Send (Chunk 9.L.1)
// -----------------------------------------------------------------------------
//
// Décisions PO 9.L.1 :
//   Q6 — Body minimal `{expected_response_by?}` (ISO datetime). Pas de
//        `{recipients}` (création hors scope ; recipients préexistants).
//   Q7 — Transitions allowed : `draft → sent` (premier envoi) + `sent → sent`
//        (idempotency retry pending recipients). `cancelled/expired` → 409.
//   Q5 — Recipients préexistants Phase 1 (SQL admin fixtures). Si zéro
//        recipient → 422 `QUOTE_REQUESTS_NO_RECIPIENTS`.
//   Q3 — Service email via fetch() Mailgun HTTP API (mode `log` par défaut
//        staging, pas de spam assureurs réels).
//   Q4 — Gating `quote_request.send` policy seedée 9.H.5 `requires_human=true,
//        agent_allowed=false` — broker JWT bypass, api_key bloquée par défaut.
//        Aucune migration nouvelle 9.L.1.
//
// Idempotency partial send :
//   - Per-recipient transition `status='pending' → 'sent'` (skip déjà `sent`).
//   - Si all recipients fail → 500 EMAIL_SEND_FAILED + QR.status inchangé.
//   - Si ≥1 recipient transition OK → QR.status='sent', sent_at populated.
//   - Re-send (`sent → sent`) retry uniquement les pending restants.

/**
 * Body POST `/v1/quote-requests/:id/send` — broker-facing.
 *
 * `expected_response_by` optional — override la valeur existante côté
 * quote_request si fournie. Format ISO datetime. NULL ne reset pas
 * l'existing value (passe la mise à jour si absent).
 */
export const SendQuoteRequestSchema = z.object({
  expected_response_by: z.string().datetime().optional(),
});
export type SendQuoteRequestInput = z.infer<typeof SendQuoteRequestSchema>;

// -----------------------------------------------------------------------------
// Write CRUD — Chunk 9.L.7
// -----------------------------------------------------------------------------
//
// Décisions PO 9.L.7 :
//   - Create QR draft + optional recipients atomique dans la même transaction
//     (broker UX naturelle : « créer demande + sélectionner 3 assureurs »).
//     Recipients liste optionnelle 0..N (max 50 — parité GET nested limite implicite).
//   - Update QR : title + expected_response_by uniquement Phase 1 (champs broker
//     éditables). status/sent_at/cancelled_* gérés par transitions dédiées.
//     Allowed sur status ∈ {draft, sent} (terminal cancelled/expired/*_received
//     interdit — protection forensic post-transition).
//   - Cancel QR : transition `draft|sent|partially_received → cancelled` avec
//     `cancelled_reason` requis 1..500 chars (parité 9.G.1 rejection_reason).
//     `fully_received/expired/cancelled` → 409 INVALID_TRANSITION.
//     Gating Couche 2 actif `quote_request.cancel` default `agent_allowed=false`
//     (seedée migration 0037).
//   - Add recipient : `insurer_code` (catalogue libre text DB 0021:245), email
//     optionnel, language default 'fr' (parité DB). Refuse duplicate
//     `(quote_request_id, insurer_code)` (unique constraint DB 0021:260).
//     Allowed sur status ∈ {draft, sent} (broker peut ajouter assureur après
//     premier send pour relance partielle).
//   - Remove recipient : DELETE physique si status='pending' uniquement.
//     Sent/responded/declined/expired refusés (audit forensic preserve).
//
// Aucune validation insurer_code contre INSURER_CODES côté Zod schema :
// catalogue assureurs est libre côté DB (text + commentaire 0021:266 « À
// reconsidérer quand D5 introduira une table catalogue »). Côté UI broker
// 9.L.8 = select restrict aux 8 INSURER_CODES connus ; côté API on accepte
// libre pour future-proof multi-catalogue.

/**
 * Body POST `/v1/quote-requests/:id/recipients` — ajouter un destinataire.
 */
export const AddQuoteRequestRecipientSchema = z.object({
  insurer_code: z.string().min(2).max(50),
  recipient_email: z.string().email().max(254).optional(),
  recipient_contact_id: UuidSchema.optional(),
  language: z.enum(["fr", "en"]).optional(),
  insurer_email_note: z.string().max(2000).nullable().optional(),
});
export type AddQuoteRequestRecipientInput = z.infer<typeof AddQuoteRequestRecipientSchema>;

/**
 * Body PATCH `/v1/quote-requests/:id/recipients/:recipientId` (9.T.5.v3.7).
 *
 * Met à jour partiellement un recipient existant sans modifier `insurer_code`
 * (les changements de couple `(quote_request_id, insurer_code)` doivent
 * transiter par DELETE + POST pour préserver l'audit trail insurer).
 *
 * Champs modifiables :
 *   - `recipient_email`  (email valide OU `null` pour effacer)
 *   - `recipient_contact_id` (UUID OU `null` pour effacer)
 *   - `language`         (`fr` | `en`)
 *
 * `.strict()` + `.refine()` : au moins un champ requis.
 */
export const UpdateQuoteRequestRecipientSchema = z
  .object({
    recipient_email: z.string().email().max(254).nullable().optional(),
    recipient_contact_id: UuidSchema.nullable().optional(),
    language: z.enum(["fr", "en"]).optional(),
    insurer_email_note: z.string().max(2000).nullable().optional(),
  })
  .strict()
  .refine(
    (data) =>
      data.recipient_email !== undefined ||
      data.recipient_contact_id !== undefined ||
      data.language !== undefined ||
      data.insurer_email_note !== undefined,
    { message: "at_least_one_field_required" },
  );
export type UpdateQuoteRequestRecipientInput = z.infer<typeof UpdateQuoteRequestRecipientSchema>;

/**
 * Body POST `/v1/quote-requests` — créer une QR draft avec recipients
 * optionnels (création atomique broker UX).
 */
export const CreateQuoteRequestSchema = z.object({
  project_id: UuidSchema,
  title: z.string().min(1).max(200).optional(),
  insurer_email_note: z.string().max(4000).nullable().optional(),
  expected_response_by: z.string().datetime().optional(),
  recipients: z.array(AddQuoteRequestRecipientSchema).max(50).optional(),
});
export type CreateQuoteRequestInput = z.infer<typeof CreateQuoteRequestSchema>;

/**
 * Body PATCH `/v1/quote-requests/:id` — édition champs broker (title +
 * expected_response_by). Status/sent_at/cancelled_* gérés par transitions
 * dédiées (send, cancel).
 */
export const UpdateQuoteRequestSchema = z
  .object({
    title: z.string().min(1).max(200).nullable().optional(),
    insurer_email_note: z.string().max(4000).nullable().optional(),
    expected_response_by: z.string().datetime().nullable().optional(),
  })
  .refine(
    (v) =>
      v.title !== undefined ||
      v.expected_response_by !== undefined ||
      v.insurer_email_note !== undefined,
    {
      message:
        "Au moins un champ requis : title, expected_response_by ou insurer_email_note (null accepté pour reset).",
    },
  );
export type UpdateQuoteRequestInput = z.infer<typeof UpdateQuoteRequestSchema>;

/**
 * Body POST `/v1/quote-requests/:id/cancel` — annuler une QR avec raison.
 * Allowed sur status ∈ {draft, sent, partially_received}. Gating Couche 2
 * actif (action_key `quote_request.cancel` default `agent_allowed=false`).
 */
export const CancelQuoteRequestSchema = z.object({
  cancelled_reason: z.string().min(1).max(500),
});
export type CancelQuoteRequestInput = z.infer<typeof CancelQuoteRequestSchema>;

/**
 * Corps de `POST /v1/quote-requests/:id/recipients/:rid/respond` et `/decline`
 * — chunk 9.AG.1.
 *
 * Les deux champs sont optionnels : consigner qu'un assureur a répondu doit être
 * possible **avant** d'avoir versé son document. Exiger `submission_id` forcerait le
 * conseiller à choisir entre « je note tout de suite » et « je note correctement »,
 * et il finirait par ne rien noter — pendant que les rappels continueraient de
 * partir.
 */
export const RecordInsurerResponseSchema = z
  .object({
    submission_id: UuidSchema.optional(),
    notes: z.string().min(1).max(1000).optional(),
  })
  .strict();
export type RecordInsurerResponseInput = z.infer<typeof RecordInsurerResponseSchema>;
